import { Box, Paper, Typography, useTheme } from "@mui/material";

import { EmissionsPieChart } from "@aclymatepackages/modules";
import {
  buildScopesWithColors,
  buildSubcategoriesArray
} from "@aclymatepackages/subcategories";
import { formatDecimal } from "@aclymatepackages/formatters";

const CARD_SX = {
  p: 2,
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: 1,
  maxWidth: 480
};

const sumTons = (records) =>
  records.reduce((sum, { tonsCo2e }) => sum + (Number(tonsCo2e) || 0), 0);

const buildScopeLegendItems = (emissions) => {
  const scopesArray = buildScopesWithColors();
  return scopesArray
    .map(({ scope, name, color }) => {
      const matching = emissions.filter(
        ({ scope: emissionScope }) => Number(emissionScope) === Number(scope)
      );
      return { label: name, color, tons: sumTons(matching) };
    })
    .filter(({ tons }) => tons > 0);
};

const buildSubcategoryLegendItems = (emissions) => {
  const subcategoryEntries = buildSubcategoriesArray(emissions);
  return subcategoryEntries
    .map(({ subcategory, name, color }) => {
      const matching = emissions.filter(
        ({ subcategory: s }) => s === subcategory
      );
      return { label: name || subcategory, color, tons: sumTons(matching) };
    })
    .filter(({ tons }) => tons > 0);
};

const buildLegendItems = (emissions, viewMode) =>
  viewMode === "scopes"
    ? buildScopeLegendItems(emissions)
    : buildSubcategoryLegendItems(emissions);

const Legend = ({ items, total }) => (
  <Box display="flex" flexDirection="column" gap={0.5}>
    {items.map(({ label, color, tons }, idx) => {
      const share = total > 0 ? (tons / total) * 100 : 0;
      return (
        <Box
          key={`legend-${idx}`}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: color,
                flexShrink: 0
              }}
            />
            <Typography variant="body2" color="textPrimary">
              {label}
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {`${formatDecimal(tons)} tCO2e (${formatDecimal(share)}%)`}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const ScoutEmissionsChart = ({
  title,
  emissions = [],
  viewMode = "scopes",
  footnote
}) => {
  const { palette } = useTheme();
  const safeEmissions = Array.isArray(emissions) ? emissions : [];

  if (safeEmissions.length === 0) {
    return (
      <Paper elevation={1} sx={CARD_SX}>
        {title && (
          <Typography variant="subtitle1" color="textPrimary">
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          No emissions data yet for this view.
        </Typography>
      </Paper>
    );
  }

  const legendItems = buildLegendItems(safeEmissions, viewMode);
  const total = sumTons(safeEmissions);

  return (
    <Paper elevation={1} sx={CARD_SX}>
      {title && (
        <Typography variant="subtitle1" color="textPrimary">
          {title}
        </Typography>
      )}
      <Box
        sx={{
          width: "100%",
          maxWidth: 320,
          alignSelf: "center",
          backgroundColor: palette.background.paper
        }}
      >
        <EmissionsPieChart
          dataArray={safeEmissions}
          viewMode={viewMode}
          aspect={1}
        />
      </Box>
      <Legend items={legendItems} total={total} />
      {footnote && (
        <Typography variant="caption" color="textSecondary">
          {footnote}
        </Typography>
      )}
    </Paper>
  );
};

export default ScoutEmissionsChart;
